import React, { useState } from 'react';
import { Building2, MapPin, TrendingUp, Target, Sparkles, Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Map, Layers, Navigation } from 'lucide-react';
interface AsIsLocation {
  address: string;
  city: string;
  type: string;
  zone: string;
  coverage: 'alta' | 'media' | 'bassa';
  yearOpened: number;
}
interface ExpansionSuggestion {
  street: string;
  city: string;
  zone: string;
  score: number;
  reasoning: string[];
  areaType: 'emergente' | 'consolidata' | 'strategica';
  competitorDensity: 'bassa' | 'media' | 'alta';
  priority: 'high' | 'medium' | 'low';
  nearbyBrands: string[];
  metrics: {
    estimatedFootTraffic: string;
    averageRent: string;
    estimatedROI: string;
    marketGrowth: string;
  };
  keyAdvantages: string[];
  strategicValue: string;
}
interface AnalysisResult {
  company: string;
  cities: string[];
  asIs: {
    totalLocations: number;
    locations: AsIsLocation[];
    coveredZones: string[];
    territorialCoverage: number;
  };
  expansion: {
    suggestions: ExpansionSuggestion[];
    emergingAreas: string[];
    strategicInsights: string[];
    uncoveredOpportunities: string[];
  };
}
const mockCompanies = [{
  id: '1',
  name: 'Luxury Fashion Brand',
  category: 'Fashion'
}, {
  id: '2',
  name: 'Tech Startup Store',
  category: 'Technology'
}, {
  id: '3',
  name: 'Gourmet Restaurant Chain',
  category: 'Food & Beverage'
}, {
  id: '4',
  name: 'Premium Jewelry',
  category: 'Luxury'
}];
const mockCities = ['Milano', 'Roma', 'Firenze', 'Venezia', 'Torino', 'Bologna', 'Napoli'];
type ViewState = 'initial' | 'loading' | 'results';
export function StrategyPlanner() {
  const [viewState, setViewState] = useState<ViewState>('initial');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [expandedSuggestion, setExpandedSuggestion] = useState<number | null>(null);
  const handleCityToggle = (city: string) => {
    setSelectedCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);
  };
  const handleAnalysis = () => {
    if (!selectedCompany || selectedCities.length === 0) return;
    setViewState('loading');
    setTimeout(() => {
      const mockResult: AnalysisResult = {
        company: mockCompanies.find(c => c.id === selectedCompany)?.name || '',
        cities: selectedCities,
        asIs: {
          totalLocations: 3,
          locations: [{
            address: 'Via Montenapoleone 8',
            city: 'Milano',
            type: 'Flagship Store',
            zone: 'Quadrilatero della Moda',
            coverage: 'alta',
            yearOpened: 2018
          }, {
            address: 'Via Condotti 45',
            city: 'Roma',
            type: 'Boutique',
            zone: 'Centro Storico',
            coverage: 'media',
            yearOpened: 2020
          }, {
            address: 'Ponte Vecchio 12',
            city: 'Firenze',
            type: 'Boutique',
            zone: 'Centro',
            coverage: 'media',
            yearOpened: 2021
          }],
          coveredZones: ['Milano - Quadrilatero della Moda', 'Roma - Centro Storico', 'Firenze - Centro'],
          territorialCoverage: 42
        },
        expansion: {
          suggestions: [{
            street: 'Via della Spiga',
            city: 'Milano',
            zone: 'Quadrilatero della Moda',
            score: 94,
            reasoning: ['Zona ad altissima densità di brand luxury complementari', 'Area già coperta ma con opportunità di rafforzamento presenza', 'Vicinanza strategica alla location esistente (300m)', 'Presenza di Gucci, Prada, Valentino nella stessa via'],
            areaType: 'consolidata',
            competitorDensity: 'alta',
            priority: 'high',
            nearbyBrands: ['Gucci', 'Prada', 'Valentino', 'Dolce & Gabbana'],
            metrics: {
              estimatedFootTraffic: '45.000 persone/giorno',
              averageRent: '€850/mq anno',
              estimatedROI: '18-24 mesi',
              marketGrowth: '+12% annuo'
            },
            keyAdvantages: ['Traffico pedonale premium garantito', 'Brand positioning ottimale', 'Sinergia con location esistente'],
            strategicValue: 'Location ideale per rafforzare la presenza nel quadrilatero e creare un effetto brand dominance nella zona più prestigiosa di Milano'
          }, {
            street: 'Via del Corso',
            city: 'Roma',
            zone: 'Tridente',
            score: 89,
            reasoning: ['Area emergente con crescente presenza di brand premium', 'Zona non ancora coperta con alto potenziale', 'Collegamento strategico tra Piazza del Popolo e Via Condotti', 'Presenza di mix retail tradizionale e luxury in evoluzione'],
            areaType: 'emergente',
            competitorDensity: 'media',
            priority: 'high',
            nearbyBrands: ['Zara', 'H&M', 'Massimo Dutti', 'COS'],
            metrics: {
              estimatedFootTraffic: '38.000 persone/giorno',
              averageRent: '€620/mq anno',
              estimatedROI: '15-20 mesi',
              marketGrowth: '+18% annuo'
            },
            keyAdvantages: ['Costo affitto competitivo', 'Area in forte crescita', 'First mover advantage nel segmento luxury'],
            strategicValue: 'Opportunità di posizionarsi come brand luxury di riferimento in una zona emergente prima della saturazione del mercato'
          }, {
            street: 'Via Tornabuoni',
            city: 'Firenze',
            zone: 'Centro Storico',
            score: 87,
            reasoning: ['Principale strada dello shopping luxury di Firenze', 'Complementare alla location esistente su Ponte Vecchio', 'Alta concentrazione di turismo internazionale alto-spendente', 'Presenza consolidata di competitor diretti'],
            areaType: 'consolidata',
            competitorDensity: 'alta',
            priority: 'medium',
            nearbyBrands: ['Ferragamo', 'Gucci', 'Prada', 'Louis Vuitton'],
            metrics: {
              estimatedFootTraffic: '32.000 persone/giorno',
              averageRent: '€720/mq anno',
              estimatedROI: '20-26 mesi',
              marketGrowth: '+8% annuo'
            },
            keyAdvantages: ['Turismo internazionale costante', 'Prestigio della location', 'Complementarità con Ponte Vecchio'],
            strategicValue: 'Rafforzamento della presenza a Firenze con una seconda location nella via più prestigiosa per il luxury retail'
          }, {
            street: 'Piazza San Marco',
            city: 'Venezia',
            zone: 'Centro Storico',
            score: 82,
            reasoning: ['Città strategica non ancora coperta', 'Location iconica con traffico turistico premium costante', 'Opportunità di essere tra i primi luxury brand in zona', 'Bassa densità di competitor diretti'],
            areaType: 'strategica',
            competitorDensity: 'bassa',
            priority: 'medium',
            nearbyBrands: ['Bottega Veneta', 'Hermès'],
            metrics: {
              estimatedFootTraffic: '28.000 persone/giorno',
              averageRent: '€780/mq anno',
              estimatedROI: '22-28 mesi',
              marketGrowth: '+10% annuo'
            },
            keyAdvantages: ['Unicità della location', 'Turismo luxury internazionale', 'Bassa competizione diretta'],
            strategicValue: 'Espansione strategica in una città iconica con presenza luxury limitata, opportunità di brand awareness internazionale'
          }],
          emergingAreas: ['Milano - Porta Nuova (nuova zona business e luxury)', 'Roma - Via Cola di Rienzo (espansione retail premium)', 'Firenze - Lungarno (zona emergente con brand internazionali)', 'Torino - Via Roma (rinnovamento urbano in corso)'],
          strategicInsights: ['Focus su città con forte componente turistica internazionale', 'Prioritizzare zone con presenza di brand complementari', 'Considerare aree emergenti prima della saturazione del mercato', 'Valutare città non ancora coperte con potenziale strategico'],
          uncoveredOpportunities: ['Venezia - nessuna presenza in città ad alto potenziale turistico', 'Torino - mercato sotto-servito nel segmento luxury', 'Bologna - centro universitario con crescente domanda premium', 'Napoli - opportunità in zona emergente del centro storico']
        }
      };
      setAnalysisResult(mockResult);
      setViewState('results');
    }, 3000);
  };
  const handleReset = () => {
    setViewState('initial');
    setSelectedCompany('');
    setSelectedCities([]);
    setAnalysisResult(null);
    setExpandedSuggestion(null);
  };
  const getCoverageColor = (coverage: string) => {
    switch (coverage) {
      case 'alta':
        return 'text-green-600 bg-green-50';
      case 'media':
        return 'text-yellow-600 bg-yellow-50';
      case 'bassa':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getAreaTypeColor = (areaType: string) => {
    switch (areaType) {
      case 'emergente':
        return 'text-blue-600 bg-blue-50';
      case 'consolidata':
        return 'text-purple-600 bg-purple-50';
      case 'strategica':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getCompetitorDensityColor = (density: string) => {
    switch (density) {
      case 'alta':
        return 'text-red-600';
      case 'media':
        return 'text-yellow-600';
      case 'bassa':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };
  if (viewState === 'initial') {
    return <div className="space-y-6" data-id="element-3501">
        <div className="flex items-center justify-between" data-id="element-3502">
          <div data-id="element-3503">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3504">
              Strategy Planner
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-3505">
              Analisi territoriale e piano strategico di espansione
            </p>
          </div>
        </div>
        <div className="bg-white p-6 border border-gray-200 rounded-lg space-y-6" data-id="element-3506">
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3507">
            <Sparkles className="w-5 h-5 text-[#1388ad] flex-shrink-0 mt-0.5" data-id="element-3508" />
            <div data-id="element-3509">
              <h3 className="text-sm font-normal text-gray-900 mb-1" data-id="element-3510">
                Pianificazione Territoriale con AI
              </h3>
              <p className="text-xs text-gray-600" data-id="element-3511">
                L'intelligenza artificiale analizzerà la presenza territoriale
                attuale dell'azienda e suggerirà le migliori opportunità di
                espansione basate su geolocalizzazione, presenza competitor,
                zone emergenti e aree strategiche.
              </p>
            </div>
          </div>
          <div className="space-y-4" data-id="element-3512">
            <div data-id="element-3513">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3514">
                Seleziona Azienda
              </label>
              <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-3515">
                <option value="" data-id="element-3516">-- Seleziona un'azienda --</option>
                {mockCompanies.map(company => <option key={company.id} value={company.id} data-id="element-3517">
                    {company.name} - {company.category}
                  </option>)}
              </select>
            </div>
            <div data-id="element-3518">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-3519">
                Seleziona Città da Analizzare
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-id="element-3520">
                {mockCities.map(city => <button key={city} onClick={() => handleCityToggle(city)} className={`px-4 py-2 text-sm rounded-lg border transition-colors ${selectedCities.includes(city) ? 'bg-[#1388ad] text-white border-[#1388ad]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#1388ad]'}`} data-id="element-3521">
                    {city}
                  </button>)}
              </div>
              {selectedCities.length > 0 && <p className="text-xs text-gray-600 mt-2" data-id="element-3522">
                  {selectedCities.length} città selezionate
                </p>}
            </div>
          </div>
          <button onClick={handleAnalysis} disabled={!selectedCompany || selectedCities.length === 0} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#1388ad] text-white text-sm font-normal hover:bg-[#0f6b8a] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors rounded-full" data-id="element-3523">
            <Map className="w-4 h-4" data-id="element-3524" />
            Genera Analisi Territoriale
          </button>
        </div>
      </div>;
  }
  if (viewState === 'loading') {
    return <div className="space-y-6" data-id="element-3525">
        <div className="flex items-center justify-between" data-id="element-3526">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3527">
            Strategy Planner
          </h1>
        </div>
        <div className="bg-white p-8 border border-gray-200 rounded-lg" data-id="element-3528">
          <div className="flex flex-col items-center justify-center py-12" data-id="element-3529">
            <div className="relative w-16 h-16 mb-6" data-id="element-3530">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full" data-id="element-3531" />
              <div className="absolute inset-0 border-4 border-[#1388ad] rounded-full border-t-transparent animate-spin" data-id="element-3532" />
              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-[#1388ad]" data-id="element-3533" />
            </div>
            <h3 className="text-lg font-normal text-gray-900 mb-2" data-id="element-3534">
              Analisi Territoriale in Corso...
            </h3>
            <p className="text-sm text-gray-600 text-center max-w-md" data-id="element-3535">
              L'AI sta analizzando la presenza territoriale e identificando le
              migliori opportunità di espansione geografica
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3536">
      <div className="flex items-center justify-between" data-id="element-3537">
        <div data-id="element-3538">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3539">
            Strategy Planner
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-id="element-3540">
            {analysisResult?.company} - {analysisResult?.cities.join(', ')}
          </p>
        </div>
        <button onClick={handleReset} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-full border border-gray-300" data-id="element-3541">
          Nuova Analisi
        </button>
      </div>
      {/* AS IS Analysis */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3542">
        <div className="p-4 border-b border-gray-200" data-id="element-3543">
          <div className="flex items-center gap-2" data-id="element-3544">
            <Map className="w-5 h-5 text-[#1388ad]" data-id="element-3545" />
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-3546">
              Analisi AS IS - Copertura Territoriale
            </h3>
          </div>
        </div>
        <div className="p-6" data-id="element-3547">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-3548">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3549">
              <div className="p-3 border-b border-gray-200 bg-white" data-id="element-3550">
                <div className="flex items-center gap-2" data-id="element-3551">
                  <MapPin className="w-4 h-4 text-[#1388ad]" data-id="element-3552" />
                  <h3 className="text-xs font-semibold text-gray-900" data-id="element-3553">
                    Location Totali
                  </h3>
                </div>
              </div>
              <div className="p-4" data-id="element-3554">
                <p className="text-3xl font-semibold text-gray-900" data-id="element-3555">
                  {analysisResult?.asIs.totalLocations}
                </p>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3556">
              <div className="p-3 border-b border-gray-200 bg-white" data-id="element-3557">
                <div className="flex items-center gap-2" data-id="element-3558">
                  <Layers className="w-4 h-4 text-[#1388ad]" data-id="element-3559" />
                  <h3 className="text-xs font-semibold text-gray-900" data-id="element-3560">
                    Zone Coperte
                  </h3>
                </div>
              </div>
              <div className="p-4" data-id="element-3561">
                <p className="text-3xl font-semibold text-gray-900" data-id="element-3562">
                  {analysisResult?.asIs.coveredZones.length}
                </p>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3563">
              <div className="p-3 border-b border-gray-200 bg-white" data-id="element-3564">
                <div className="flex items-center gap-2" data-id="element-3565">
                  <Navigation className="w-4 h-4 text-[#1388ad]" data-id="element-3566" />
                  <h3 className="text-xs font-semibold text-gray-900" data-id="element-3567">
                    Copertura Territoriale
                  </h3>
                </div>
              </div>
              <div className="p-4" data-id="element-3568">
                <p className="text-3xl font-semibold text-gray-900" data-id="element-3569">
                  {analysisResult?.asIs.territorialCoverage}%
                </p>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-3570">
            <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-3571">
              Zone Attualmente Coperte
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-3572">
              {analysisResult?.asIs.coveredZones.map((zone, index) => <span key={index} className="px-3 py-1.5 bg-[#1388ad]/10 text-[#1388ad] rounded-full text-xs font-normal" data-id="element-3573">
                  {zone}
                </span>)}
            </div>
          </div>
          <div className="space-y-3" data-id="element-3574">
            {analysisResult?.asIs.locations.map((location, index) => <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors" data-id="element-3575">
                <div className="flex items-start justify-between gap-4" data-id="element-3576">
                  <div className="flex-1" data-id="element-3577">
                    <div className="flex items-center gap-3 mb-2" data-id="element-3578">
                      <MapPin className="w-4 h-4 text-[#1388ad]" data-id="element-3579" />
                      <div data-id="element-3580">
                        <h3 className="text-sm font-normal text-gray-900" data-id="element-3581">
                          {location.address}
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-3582">
                          {location.city} - {location.zone}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-600" data-id="element-3583">
                      <span className="px-2 py-1 bg-gray-100 rounded-full" data-id="element-3584">
                        {location.type}
                      </span>
                      <span data-id="element-3585">Aperto dal {location.yearOpened}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-normal ${getCoverageColor(location.coverage)}`} data-id="element-3586">
                    Copertura{' '}
                    {location.coverage === 'alta' ? 'Alta' : location.coverage === 'media' ? 'Media' : 'Bassa'}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Expansion Plan */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3587">
        <div className="p-4 border-b border-gray-200" data-id="element-3588">
          <div className="flex items-center gap-2" data-id="element-3589">
            <TrendingUp className="w-5 h-5 text-[#1388ad]" data-id="element-3590" />
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-3591">
              Piano di Espansione Territoriale
            </h3>
          </div>
        </div>
        <div className="p-6" data-id="element-3592">
          <div className="space-y-6" data-id="element-3593">
            {analysisResult?.expansion.suggestions.map((suggestion, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-3594">
                {/* Header con Score */}
                <div className="p-5 bg-gradient-to-r from-gray-50 to-white" data-id="element-3595">
                  <div className="flex items-start justify-between gap-4 mb-4" data-id="element-3596">
                    <div className="flex-1" data-id="element-3597">
                      <div className="flex items-center gap-3 mb-2" data-id="element-3598">
                        <MapPin className="w-5 h-5 text-[#1388ad]" data-id="element-3599" />
                        <div data-id="element-3600">
                          <h3 className="text-lg font-semibold text-gray-900" data-id="element-3601">
                            {suggestion.street}
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-3602">
                            {suggestion.city} - {suggestion.zone}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-3603">
                        <span className={`px-3 py-1 rounded-full text-xs font-normal ${getPriorityColor(suggestion.priority)}`} data-id="element-3604">
                          Priorità{' '}
                          {suggestion.priority === 'high' ? 'Alta' : suggestion.priority === 'medium' ? 'Media' : 'Bassa'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-normal ${getAreaTypeColor(suggestion.areaType)}`} data-id="element-3605">
                          {suggestion.areaType === 'emergente' ? 'Area Emergente' : suggestion.areaType === 'consolidata' ? 'Area Consolidata' : 'Area Strategica'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-3606">
                      <div className="text-3xl font-semibold text-[#1388ad] mb-1" data-id="element-3607">
                        {suggestion.score}%
                      </div>
                      <div className="text-xs text-gray-600 uppercase tracking-wide" data-id="element-3608">
                        Compatibilità
                      </div>
                    </div>
                  </div>
                  {/* Barra di compatibilità visiva */}
                  <div className="space-y-2" data-id="element-3609">
                    <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-3610">
                      <span data-id="element-3611">Score di Compatibilità</span>
                      <span className="font-semibold" data-id="element-3612">
                        {suggestion.score}/100
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-3613">
                      <div className="h-full bg-gradient-to-r from-[#1388ad] to-[#0f6b8a] rounded-full transition-all duration-500" style={{
                    width: `${suggestion.score}%`
                  }} data-id="element-3614" />
                    </div>
                  </div>
                </div>
                {/* Valore Strategico */}
                <div className="px-5 py-4 bg-blue-50 border-y border-blue-100" data-id="element-3615">
                  <div className="flex items-start gap-3" data-id="element-3616">
                    <Target className="w-5 h-5 text-[#1388ad] flex-shrink-0 mt-0.5" data-id="element-3617" />
                    <div data-id="element-3618">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-3619">
                        Valore Strategico
                      </h4>
                      <p className="text-sm text-gray-700" data-id="element-3620">
                        {suggestion.strategicValue}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Vantaggi Chiave */}
                <div className="px-5 py-4 border-b border-gray-200" data-id="element-3621">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-3622">
                    <CheckCircle className="w-4 h-4 text-[#1388ad]" data-id="element-3623" />
                    Vantaggi Chiave
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-3624">
                    {suggestion.keyAdvantages.map((advantage, idx) => <div key={idx} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200" data-id="element-3625">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-3626" />
                        <span className="text-xs text-gray-700" data-id="element-3627">
                          {advantage}
                        </span>
                      </div>)}
                  </div>
                </div>
                {/* Metriche Chiave */}
                <div className="px-5 py-4 bg-gray-50" data-id="element-3628">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-3629">
                    Metriche di Performance
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3630">
                    <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-3631">
                      <div className="text-xs text-gray-600 mb-1" data-id="element-3632">
                        Traffico Stimato
                      </div>
                      <div className="text-sm font-semibold text-gray-900" data-id="element-3633">
                        {suggestion.metrics.estimatedFootTraffic}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-3634">
                      <div className="text-xs text-gray-600 mb-1" data-id="element-3635">
                        Affitto Medio
                      </div>
                      <div className="text-sm font-semibold text-gray-900" data-id="element-3636">
                        {suggestion.metrics.averageRent}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-3637">
                      <div className="text-xs text-gray-600 mb-1" data-id="element-3638">
                        ROI Stimato
                      </div>
                      <div className="text-sm font-semibold text-gray-900" data-id="element-3639">
                        {suggestion.metrics.estimatedROI}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-3640">
                      <div className="text-xs text-gray-600 mb-1" data-id="element-3641">
                        Crescita Mercato
                      </div>
                      <div className="text-sm font-semibold text-green-600" data-id="element-3642">
                        {suggestion.metrics.marketGrowth}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Dettagli Espandibili */}
                <div className="border-t border-gray-200" data-id="element-3643">
                  <button onClick={() => setExpandedSuggestion(expandedSuggestion === index ? null : index)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-3644">
                    <span className="text-sm font-normal text-gray-700" data-id="element-3645">
                      Analisi Dettagliata
                    </span>
                    {expandedSuggestion === index ? <ChevronUp className="w-4 h-4 text-gray-500" data-id="element-3646" /> : <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-3647" />}
                  </button>
                  {expandedSuggestion === index && <div className="px-5 pb-5 space-y-4 bg-gray-50" data-id="element-3648">
                      <div className="pt-4" data-id="element-3649">
                        <div className="flex items-center gap-2 mb-3" data-id="element-3650">
                          <Target className="w-4 h-4 text-[#1388ad]" data-id="element-3651" />
                          <h4 className="text-sm font-semibold text-gray-900" data-id="element-3652">
                            Motivazioni Strategiche
                          </h4>
                        </div>
                        <ul className="space-y-2" data-id="element-3653">
                          {suggestion.reasoning.map((reason, idx) => <li key={idx} className="text-sm text-gray-600 flex items-start gap-2 p-2 bg-white rounded border border-gray-200" data-id="element-3654">
                              <CheckCircle className="w-4 h-4 text-[#1388ad] flex-shrink-0 mt-0.5" data-id="element-3655" />
                              <span data-id="element-3656">{reason}</span>
                            </li>)}
                        </ul>
                      </div>
                      <div data-id="element-3657">
                        <div className="flex items-center gap-2 mb-3" data-id="element-3658">
                          <Building2 className="w-4 h-4 text-[#1388ad]" data-id="element-3659" />
                          <h4 className="text-sm font-semibold text-gray-900" data-id="element-3660">
                            Contesto Competitivo
                          </h4>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-3661">
                          <div className="flex items-center justify-between mb-2" data-id="element-3662">
                            <span className="text-xs text-gray-600" data-id="element-3663">
                              Densità Competitor
                            </span>
                            <span className={`text-xs font-semibold ${getCompetitorDensityColor(suggestion.competitorDensity)}`} data-id="element-3664">
                              {suggestion.competitorDensity === 'alta' ? 'Alta' : suggestion.competitorDensity === 'media' ? 'Media' : 'Bassa'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3" data-id="element-3665">
                            {suggestion.nearbyBrands.map((brand, idx) => <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs" data-id="element-3666">
                                {brand}
                              </span>)}
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Emerging Areas & Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3667">
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-3668">
          <div className="p-4 border-b border-gray-200" data-id="element-3669">
            <div className="flex items-center gap-2" data-id="element-3670">
              <TrendingUp className="w-5 h-5 text-[#1388ad]" data-id="element-3671" />
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-3672">
                Aree Emergenti
              </h3>
            </div>
          </div>
          <div className="p-4" data-id="element-3673">
            <ul className="space-y-2" data-id="element-3674">
              {analysisResult?.expansion.emergingAreas.map((area, index) => <li key={index} className="text-sm text-gray-600 flex items-start gap-2" data-id="element-3675">
                  <span className="text-[#1388ad] mt-0.5" data-id="element-3676">•</span>
                  <span data-id="element-3677">{area}</span>
                </li>)}
            </ul>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200" data-id="element-3678">
          <div className="p-4 border-b border-gray-200" data-id="element-3679">
            <div className="flex items-center gap-2" data-id="element-3680">
              <AlertCircle className="w-5 h-5 text-[#1388ad]" data-id="element-3681" />
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-3682">
                Opportunità Non Coperte
              </h3>
            </div>
          </div>
          <div className="p-4" data-id="element-3683">
            <ul className="space-y-2" data-id="element-3684">
              {analysisResult?.expansion.uncoveredOpportunities.map((opp, index) => <li key={index} className="text-sm text-gray-600 flex items-start gap-2" data-id="element-3685">
                    <span className="text-[#1388ad] mt-0.5" data-id="element-3686">•</span>
                    <span data-id="element-3687">{opp}</span>
                  </li>)}
            </ul>
          </div>
        </div>
      </div>
      {/* Strategic Insights */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-3688">
        <div className="p-4 border-b border-gray-200" data-id="element-3689">
          <div className="flex items-center gap-2" data-id="element-3690">
            <CheckCircle className="w-5 h-5 text-[#1388ad]" data-id="element-3691" />
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-3692">
              Insight Strategici
            </h3>
          </div>
        </div>
        <div className="p-4" data-id="element-3693">
          <ul className="space-y-2" data-id="element-3694">
            {analysisResult?.expansion.strategicInsights.map((insight, index) => <li key={index} className="text-sm text-gray-600 flex items-start gap-2" data-id="element-3695">
                  <span className="text-[#1388ad] mt-0.5" data-id="element-3696">•</span>
                  <span data-id="element-3697">{insight}</span>
                </li>)}
          </ul>
        </div>
      </div>
    </div>;
}